import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Users, School, CheckSquare, TrendingUp, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { useAuth } from '../../contexts/AuthContext';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalSchools: 0,
    totalUsers: 0,
    totalMeals: 0,
    todayAttendance: 0,
    currentDate: '',
    pendingTasks: 0,
    recentActivities: []
  });

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setStats({
        totalSchools: 234,
        totalUsers: 1847,
        totalMeals: 45678,
        todayAttendance: 1234,
        currentDate: '2080-10-15',
        pendingTasks: 8,
        recentActivities: [
          { id: 1, type: 'attendance', message: 'Attendance submitted for Shree School', time: '2 mins ago' },
          { id: 2, type: 'meal', message: 'Meal request approved for Central Branch', time: '15 mins ago' },
          { id: 3, type: 'user', message: 'New user registered: Ram Bahadur', time: '1 hour ago' },
        ]
      });
    }, 1000);
  }, []);

  const statCards = [
    {
      title: 'Total Schools',
      value: stats.totalSchools,
      icon: School,
      color: 'blue',
      change: '+12%',
      changeType: 'positive'
    },
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: Users,
      color: 'primary',
      change: '+8%',
      changeType: 'positive'
    },
    {
      title: 'Total Meals',
      value: stats.totalMeals.toLocaleString(),
      icon: CheckSquare,
      color: 'success',
      change: '+23%',
      changeType: 'positive'
    },
    {
      title: "Today's Attendance",
      value: stats.todayAttendance.toLocaleString(),
      icon: TrendingUp,
      color: 'warning',
      change: '+5%',
      changeType: 'positive'
    }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'attendance': return CheckSquare;
      case 'meal': return CheckSquare;
      case 'user': return Users;
      default: return Clock;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'attendance': return 'bg-blue-100 text-blue-600';
      case 'meal': return 'bg-success-100 text-success-600';
      case 'user': return 'bg-primary-100 text-primary-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Welcome back, {user?.full_name}! 👋
            </h1>
            <p className="text-gray-600 mt-2">
              Here's what's happening with your meal management system today.
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Badge variant="info" className="text-sm px-3 py-1">
              Date: {stats.currentDate} (BS)
            </Badge>
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card hover className="relative overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                      <div className="flex items-center mt-2">
                        <span className={`text-sm font-medium ${
                          stat.changeType === 'positive' ? 'text-success' : 'text-error'
                        }`}>
                          {stat.change}
                        </span>
                        <span className="text-sm text-gray-500 ml-1">from last month</span>
                      </div>
                    </div>
                    <div className={`w-12 h-12 bg-${stat.color}-100 rounded-lg flex items-center justify-center`}>
                      <Icon className={`text-${stat.color}-600`} size={24} />
                    </div>
                  </div>
                </CardContent>
                {/* Decorative gradient */}
                <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-${stat.color}-50 to-transparent rounded-bl-full opacity-50`} />
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="lg:col-span-2"
        >
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card hover className="p-4 text-center cursor-pointer">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <CheckSquare className="text-primary" size={24} />
                    </div>
                    <h3 className="font-semibold text-gray-900">Submit Attendance</h3>
                    <p className="text-sm text-gray-600 mt-1">Mark daily attendance</p>
                  </Card>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card hover className="p-4 text-center cursor-pointer">
                    <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <Users className="text-success" size={24} />
                    </div>
                    <h3 className="font-semibold text-gray-900">Record Meals</h3>
                    <p className="text-sm text-gray-600 mt-1">Log meal distribution</p>
                  </Card>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card hover className="p-4 text-center cursor-pointer">
                    <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <TrendingUp className="text-accent" size={24} />
                    </div>
                    <h3 className="font-semibold text-gray-900">View Reports</h3>
                    <p className="text-sm text-gray-600 mt-1">Analytics & insights</p>
                  </Card>
                </motion.div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {stats.recentActivities.map((activity, index) => {
                  const Icon = getActivityIcon(activity.type);
                  return (
                    <motion.div
                      key={activity.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.6 + index * 0.1 }}
                      className="flex items-start space-x-3"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getActivityColor(activity.type)}`}>
                        <Icon size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{activity.message}</p>
                        <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
